import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Bell, Send, CalendarDays, CheckCircle, Plus, Trash2,
  MessageCircle, MapPin, Package, Truck, Phone, Mail, FileText, Printer, Download, ExternalLink, Filter
} from "lucide-react";
import { ConfirmAction } from "@/components/ConfirmAction";

export function RemindersTabContent({
  upcomingTrips,
  sendingReminders,
  sendAllReminders,
  sendSingleReminder,
}) {
  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Bell className="inline w-5 h-5 mr-2" />
              Automated Reminders
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              WhatsApp reminders are sent automatically 24 hours before each trip
            </p>
          </div>
          <Button onClick={sendAllReminders} disabled={sendingReminders} className="rounded-full bg-green-600 hover:bg-green-700">
            {sendingReminders ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send All Pending Reminders
              </>
            )}
          </Button>
        </CardHeader>
      </Card>

      <Card className="border-0 shadow-lg rounded-2xl">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
            <CalendarDays className="inline w-5 h-5 mr-2" />
            Upcoming Trips (Next 7 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(upcomingTrips).length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Loading upcoming trips...</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(upcomingTrips).map(([date, data]) => (
                <div key={date} className="border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-bold text-lg">{date}</h4>
                      <p className="text-sm text-muted-foreground">
                        {data.individual_count} individual + {data.group_count} group bookings
                        ({data.total_passengers} total passengers)
                      </p>
                    </div>
                    {(data.individual_count > 0 || data.group_count > 0) && (
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        {data.total_passengers} passengers
                      </Badge>
                    )}
                  </div>
                  
                  {data.individual_count === 0 && data.group_count === 0 ? (
                    <p className="text-sm text-muted-foreground italic">No trips scheduled</p>
                  ) : (
                    <div className="space-y-2">
                      {data.individual_bookings?.slice(0, 5).map((booking) => (
                        <div key={booking.id} className="flex items-center justify-between bg-muted/30 rounded-lg p-3">
                          <div>
                            <p className="font-medium">{booking.full_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {booking.travel_time} | {booking.pickup_location?.substring(0, 25)}...
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {booking.reminder_sent ? (
                              <Badge variant="outline" className="text-green-600 border-green-200">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Reminded
                              </Badge>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => sendSingleReminder(booking.id)} className="text-xs">
                                <Bell className="w-3 h-3 mr-1" />
                                Send
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {data.group_bookings?.slice(0, 5).map((booking) => (
                        <div key={booking.id} className="flex items-center justify-between bg-yellow-50 rounded-lg p-3">
                          <div>
                            <p className="font-medium">
                              {booking.organizer_name}
                              <Badge variant="secondary" className="ml-2 text-xs bg-yellow-200">
                                Group ({booking.num_passengers})
                              </Badge>
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {booking.travel_time} | {booking.pickup_location?.substring(0, 25)}...
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {booking.reminder_sent ? (
                              <Badge variant="outline" className="text-green-600 border-green-200">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Reminded
                              </Badge>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => sendSingleReminder(booking.id)} className="text-xs">
                                <Bell className="w-3 h-3 mr-1" />
                                Send
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function WhatsAppGroupsTabContent({
  whatsappGroups,
  newGroup, setNewGroup,
  addWhatsAppGroup,
  deleteWhatsAppGroup,
}) {
  return (
    <Card className="border-0 shadow-lg rounded-2xl">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <MessageCircle className="w-5 h-5 text-green-600" />
            WhatsApp Groups by Travel Date
          </CardTitle>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Create WhatsApp groups for each travel date. Passengers will receive the invite link in their confirmation email.
        </p>
      </CardHeader>
      <CardContent>
        <div className="p-4 bg-green-50 rounded-xl border border-green-200 mb-6">
          <h4 className="font-semibold text-green-800 mb-3">Add WhatsApp Group</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Travel Date *</Label>
              <Input type="date" value={newGroup.date} onChange={(e) => setNewGroup({...newGroup, date: e.target.value})} className="h-10" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">WhatsApp Invite Link *</Label>
              <Input value={newGroup.invite_link} onChange={(e) => setNewGroup({...newGroup, invite_link: e.target.value})} placeholder="https://chat.whatsapp.com/..." className="h-10" />
            </div>
            <div className="flex items-end">
              <Button onClick={addWhatsAppGroup} className="w-full bg-green-600 hover:bg-green-700 h-10">
                <Plus className="w-4 h-4 mr-1" />
                Add Group
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            How to get invite link: Open WhatsApp group - Group Info - Invite via link - Copy link
          </p>
        </div>

        <div className="space-y-3">
          {whatsappGroups.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No WhatsApp groups created yet</p>
              <p className="text-sm text-muted-foreground">Create a group for each travel date above</p>
            </div>
          ) : (
            whatsappGroups.map((group) => (
              <div key={group.id} className="flex items-center justify-between p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold">{group.date}</p>
                    <a href={group.invite_link} target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 hover:underline truncate max-w-[300px] block">
                      {group.invite_link}
                    </a>
                    {group.description && <p className="text-xs text-muted-foreground">{group.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(group.invite_link); }}>
                    Copy Link
                  </Button>
                  <ConfirmAction
                    title="Delete WhatsApp Group?"
                    description={`Delete the WhatsApp group for ${group.date}? This only removes it from the dashboard, not the actual WhatsApp group.`}
                    onConfirm={() => deleteWhatsAppGroup(group.id)}
                    confirmText="Yes, Delete"
                    variant="destructive"
                  >
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" data-testid={`delete-whatsapp-group-${group.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </ConfirmAction>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function DestinationsTabContent({
  destinations,
  isAddingDestination, setIsAddingDestination,
  newDestination, setNewDestination,
  addDestination,
  deleteDestination,
}) {
  return (
    <Card className="border-0 shadow-lg rounded-2xl">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Manage Destinations</CardTitle>
        <Dialog open={isAddingDestination} onOpenChange={setIsAddingDestination}>
          <DialogTrigger asChild>
            <Button data-testid="add-destination-btn" className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              Add Destination
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Destination</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input value={newDestination.name} onChange={(e) => setNewDestination(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Wisconsin Dells" data-testid="destination-name-input" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={newDestination.description} onChange={(e) => setNewDestination(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description of the destination" data-testid="destination-description-input" />
              </div>
              <div>
                <Label>Image URL (optional)</Label>
                <Input value={newDestination.image_url} onChange={(e) => setNewDestination(prev => ({ ...prev, image_url: e.target.value }))} placeholder="https://..." data-testid="destination-image-input" />
              </div>
              <Button onClick={addDestination} data-testid="save-destination-btn" className="w-full">
                Save Destination
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="destinations-list">
          {destinations.filter(d => d.is_active).map((dest) => (
            <Card key={dest.id} className="overflow-hidden">
              <div className="h-32 bg-cover bg-center" style={{ backgroundImage: `url('${dest.image_url || "https://images.unsplash.com/photo-1590058572286-ccc2665c502e"}')` }} />
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-bold">{dest.name}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-2">{dest.description}</p>
                  </div>
                  <ConfirmAction
                    title="Delete Destination?"
                    description={`Remove "${dest.name}" from the destinations list?`}
                    onConfirm={() => deleteDestination(dest.id)}
                    confirmText="Yes, Delete"
                    variant="destructive"
                  >
                    <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" data-testid={`delete-destination-${dest.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </ConfirmAction>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


export function ShippingTabContent({ shippingRequests, updateShippingStatus, shippingPrices, bolSubmissions }) {
  const [manifestDate, setManifestDate] = useState(new Date().toISOString().split("T")[0]);
  const BOL_PDF_URL = "https://customer-assets.emergentagent.com/job_99515ecf-0f57-43af-8b37-430c3fa3784d/artifacts/hwqavab7_T%20%26%20M%20Courier%20Bill-of-lading.pdf";

  const statusBadge = (status) => {
    const map = {
      pending: "bg-yellow-100 text-yellow-700",
      confirmed: "bg-blue-100 text-blue-700",
      picked_up: "bg-purple-100 text-purple-700",
      delivered: "bg-green-100 text-green-700",
      cancelled: "bg-red-100 text-red-700"
    };
    return <Badge className={map[status] || "bg-gray-100"}>{status}</Badge>;
  };

  const formatItems = (items) => {
    if (!items) return "-";
    return Object.entries(items)
      .filter(([_, v]) => v > 0)
      .map(([k, v]) => `${v} ${k}`)
      .join(", ");
  };

  // Calculate estimated cost for a shipping request using admin-set prices
  const sp = shippingPrices || {};
  const calcCost = (items) => {
    if (!items) return 0;
    return (items.suitcases || 0) * (sp.ship_suitcase || 0)
      + (items.barrels || 0) * (sp.ship_barrel || 0)
      + (items.boxes || 0) * (sp.ship_box || 0)
      + (items.bicycles || 0) * (sp.ship_bicycle || 0)
      + (items.scooters || 0) * (sp.ship_scooter || 0)
      + (items.tvs || 0) * (sp.ship_tv || 0)
      + (items.miscellaneous || 0) * (sp.ship_misc || 0);
  };

  // Filter requests for manifest date
  const manifestRequests = shippingRequests.filter(r => r.preferred_date === manifestDate);

  // Print a shipping invoice for a Bill of Lading submission
  const printShippingInvoice = (bol, prices) => {
    // Parse items from description to compute costs
    const desc = (bol.itemsDescription || "").toLowerCase();
    const parseCount = (keyword) => {
      const match = desc.match(new RegExp(`(\\d+)\\s*${keyword}`, "i"));
      return match ? parseInt(match[1]) : 0;
    };
    const items = [
      { name: "Suitcase", qty: parseCount("suitcase"), rate: prices.ship_suitcase || 0 },
      { name: "Barrel", qty: parseCount("barrel"), rate: prices.ship_barrel || 0 },
      { name: "Box", qty: parseCount("box"), rate: prices.ship_box || 0 },
      { name: "Bicycle", qty: parseCount("bicycl"), rate: prices.ship_bicycle || 0 },
      { name: "Scooter", qty: parseCount("scooter"), rate: prices.ship_scooter || 0 },
      { name: "TV", qty: parseCount("tv"), rate: prices.ship_tv || 0 },
    ].filter(i => i.qty > 0);
    const subtotal = items.reduce((s, i) => s + i.qty * i.rate, 0);

    const rows = items.map(i =>
      `<tr><td>${i.name}</td><td class="center">${i.qty}</td><td class="right">$${i.rate.toFixed(2)}</td><td class="right">$${(i.qty * i.rate).toFixed(2)}</td></tr>`
    ).join("") || `<tr><td colspan="4" class="center">Items from description: ${bol.itemsDescription || "N/A"}</td></tr>`;

    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Shipping Invoice — ${bol.bolNumber || bol.id}</title>
      <style>body{font-family:system-ui;padding:30px;max-width:700px;margin:0 auto}
      h1{color:#ea580c;font-size:22px;margin-bottom:4px}
      .meta{color:#666;font-size:13px;margin-bottom:20px}
      table{width:100%;border-collapse:collapse;margin-top:12px;font-size:13px}
      th{background:#f97316;color:#fff;padding:8px;text-align:left}
      td{padding:8px;border-bottom:1px solid #e5e7eb}
      .right{text-align:right}.center{text-align:center}
      .total-row td{font-weight:bold;border-top:2px solid #333;font-size:15px}
      .section{margin-top:20px;padding:12px;background:#fef3c7;border-radius:8px;font-size:13px}
      .section h3{margin:0 0 6px;font-size:14px;color:#92400e}
      @media print{button{display:none}}</style></head><body>`);
    w.document.write(`<h1>T&M Courier Services</h1>`);
    w.document.write(`<div class="meta">(815) 914-8139 | Tmcourierservice1@gmail.com</div>`);
    w.document.write(`<h2 style="font-size:18px">Shipping Invoice</h2>`);
    w.document.write(`<div class="meta">Invoice #: ${bol.bolNumber || bol.id} &nbsp;|&nbsp; Date: ${bol.date || "N/A"}</div>`);
    w.document.write(`<div class="section"><h3>Sender</h3>${bol.shipFromName}<br>${bol.shipFromAddress || ""} ${bol.shipFromCityStateZip || ""}<br>Phone: ${bol.shipFromPhone || "N/A"}</div>`);
    w.document.write(`<div class="section"><h3>Receiver (Customs Pickup)</h3>${bol.receiverName}<br>${bol.receiverAddress || ""} ${bol.receiverCityStateZip || ""}<br>Phone 1: ${bol.receiverPhone1 || "N/A"}<br>Phone 2: ${bol.receiverPhone2 || "N/A"}</div>`);
    w.document.write(`<table><thead><tr><th>Item</th><th class="center">Qty</th><th class="right">Rate</th><th class="right">Total</th></tr></thead><tbody>${rows}</tbody>`);
    if (items.length > 0) {
      w.document.write(`<tbody><tr class="total-row"><td colspan="3">TOTAL</td><td class="right">$${subtotal.toFixed(2)}</td></tr></tbody>`);
    }
    w.document.write(`</table>`);
    if (bol.specialInstructions) w.document.write(`<div class="section"><h3>Special Instructions</h3><pre style="white-space:pre-wrap;font-family:inherit;margin:0">${bol.specialInstructions}</pre></div>`);
    w.document.write(`<br><button onclick="window.print()">Print Invoice</button></body></html>`);
    w.document.close();
  };

  const printManifest = () => {
    const rows = manifestRequests.map(r =>
      `<tr><td>${r.full_name}</td><td>${r.phone_number}</td><td>${r.pickup_address}</td><td>${r.dropoff_address}</td><td>${formatItems(r.items)}</td><td>${r.total_items}</td><td>$${calcCost(r.items).toFixed(2)}</td><td>${r.status}</td></tr>`
    ).join("");
    const totalItems = manifestRequests.reduce((s, r) => s + (r.total_items || 0), 0);
    const totalCost = manifestRequests.reduce((s, r) => s + calcCost(r.items), 0);
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Shipping Manifest — ${manifestDate}</title><style>body{font-family:system-ui;padding:20px}table{width:100%;border-collapse:collapse;font-size:13px}th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}th{background:#f97316;color:#fff}tfoot td{font-weight:bold;background:#fef3c7}.header{display:flex;justify-content:space-between;align-items:center;margin-bottom:16px}.logo{font-size:22px;font-weight:bold;color:#ea580c}@media print{button{display:none}}</style></head><body>`);
    w.document.write(`<div class="header"><div class="logo">T&M Courier — Shipping Manifest</div><div><strong>Date:</strong> ${manifestDate} &nbsp; <strong>Total Shipments:</strong> ${manifestRequests.length}</div></div>`);
    w.document.write(`<table><thead><tr><th>Customer</th><th>Phone</th><th>Pickup</th><th>Delivery</th><th>Items</th><th>Qty</th><th>Est. Cost</th><th>Status</th></tr></thead><tbody>${rows}</tbody>`);
    w.document.write(`<tfoot><tr><td colspan="5">TOTALS</td><td>${totalItems}</td><td>$${totalCost.toFixed(2)}</td><td>${manifestRequests.length} shipments</td></tr></tfoot></table>`);
    w.document.write(`<br><button onclick="window.print()">Print</button></body></html>`);
    w.document.close();
  };

  return (
    <div className="space-y-4">
      {/* Quick links row: uploaded BoL PDF + pricing summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-0 shadow-md rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-bold text-sm text-orange-900 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> T&M Courier Bill of Lading Template
                </h4>
                <p className="text-[11px] text-orange-700 mt-1">Your uploaded BoL document — download or view</p>
              </div>
              <div className="flex gap-2">
                <a href={BOL_PDF_URL} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" className="text-orange-700 border-orange-300 hover:bg-orange-100" data-testid="view-bol-pdf">
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> View PDF
                  </Button>
                </a>
                <a href={BOL_PDF_URL} download>
                  <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="download-bol-pdf">
                    <Download className="w-3.5 h-3.5 mr-1" /> Download
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md rounded-2xl">
          <CardContent className="p-4">
            <h4 className="font-bold text-sm text-gray-700 flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-orange-600" /> Shipping Rates
            </h4>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
              {[
                ["Suitcase", sp.ship_suitcase],
                ["Barrel", sp.ship_barrel],
                ["Box", sp.ship_box],
                ["Bicycle", sp.ship_bicycle],
                ["Scooter", sp.ship_scooter],
                ["TV", sp.ship_tv],
                ["Misc", sp.ship_misc],
              ].filter(([, v]) => v > 0).map(([label, val]) => (
                <span key={label} className="bg-orange-50 px-2 py-0.5 rounded-full border border-orange-100 font-medium">
                  {label}: <span className="text-orange-700">${val}</span>
                </span>
              ))}
              {Object.values(sp).every(v => !v || v === 0) && (
                <span className="text-muted-foreground italic">No shipping prices set — configure in Settings</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Shipping Manifest */}
      <Card className="border-0 shadow-lg rounded-2xl" data-testid="shipping-manifest-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Printer className="w-5 h-5 text-orange-600" />
              Shipping Manifest
            </CardTitle>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={manifestDate}
                onChange={(e) => setManifestDate(e.target.value)}
                className="h-8 text-xs w-40"
                data-testid="manifest-date-input"
              />
              <Badge className="bg-orange-100 text-orange-700 text-xs">
                {manifestRequests.length} shipment{manifestRequests.length !== 1 ? "s" : ""}
              </Badge>
              <Button size="sm" variant="outline" onClick={printManifest} disabled={manifestRequests.length === 0}
                className="text-orange-700 border-orange-300" data-testid="print-shipping-manifest">
                <Printer className="w-3.5 h-3.5 mr-1" /> Print
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {manifestRequests.length === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground">
              <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              No shipments scheduled for {manifestDate}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-orange-50/50">
                    <th className="p-2 text-left font-medium">Customer</th>
                    <th className="p-2 text-left font-medium">From</th>
                    <th className="p-2 text-left font-medium">To</th>
                    <th className="p-2 text-left font-medium">Items</th>
                    <th className="p-2 text-right font-medium">Est. Cost</th>
                    <th className="p-2 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {manifestRequests.map(r => (
                    <tr key={r.id} className="border-b hover:bg-orange-50/30">
                      <td className="p-2">
                        <p className="font-medium">{r.full_name}</p>
                        <p className="text-[10px] text-muted-foreground">{r.phone_number}</p>
                      </td>
                      <td className="p-2 max-w-[120px] truncate">{r.pickup_address}</td>
                      <td className="p-2 max-w-[120px] truncate">{r.dropoff_address}</td>
                      <td className="p-2">{formatItems(r.items) || "-"}</td>
                      <td className="p-2 text-right font-semibold text-orange-700">${calcCost(r.items).toFixed(2)}</td>
                      <td className="p-2">{statusBadge(r.status)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-orange-50 font-semibold text-xs">
                    <td className="p-2" colSpan={3}>Total: {manifestRequests.length} shipment{manifestRequests.length !== 1 ? "s" : ""}</td>
                    <td className="p-2">{manifestRequests.reduce((s, r) => s + (r.total_items || 0), 0)} items</td>
                    <td className="p-2 text-right text-orange-700">${manifestRequests.reduce((s, r) => s + calcCost(r.items), 0).toFixed(2)}</td>
                    <td className="p-2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Shipping Requests */}
      <Card className="border-0 shadow-lg rounded-2xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Truck className="w-5 h-5 text-orange-600" />
              All Shipping Requests ({shippingRequests.length})
            </CardTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="w-3 h-3" /> (815) 914-8139
              <span className="mx-1">|</span>
              <Mail className="w-3 h-3" /> Tmcourierservice1@gmail.com
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {shippingRequests.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No shipping requests yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-orange-50/50">
                    <th className="p-3 text-left font-medium">Customer</th>
                    <th className="p-3 text-left font-medium">Pickup</th>
                    <th className="p-3 text-left font-medium">Delivery</th>
                    <th className="p-3 text-left font-medium">Items</th>
                    <th className="p-3 text-right font-medium">Est. Cost</th>
                    <th className="p-3 text-left font-medium">Date</th>
                    <th className="p-3 text-left font-medium">Status</th>
                    <th className="p-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shippingRequests.map((req) => (
                    <tr key={req.id} className="border-b hover:bg-orange-50/30" data-testid={`shipping-row-${req.id}`}>
                      <td className="p-3">
                        <p className="font-medium">{req.full_name}</p>
                        <p className="text-xs text-muted-foreground">{req.phone_number}</p>
                        {req.booking_confirmation && (
                          <Badge className="text-[10px] bg-blue-50 text-blue-700 border-blue-200 mt-1">
                            Booking #{req.booking_confirmation}
                          </Badge>
                        )}
                        {req.email && <p className="text-xs text-muted-foreground">{req.email}</p>}
                      </td>
                      <td className="p-3 text-xs max-w-[150px] truncate">{req.pickup_address}</td>
                      <td className="p-3 text-xs max-w-[150px] truncate">{req.dropoff_address}</td>
                      <td className="p-3">
                        <p className="text-xs font-medium">{req.total_items} items</p>
                        <p className="text-[10px] text-muted-foreground">{formatItems(req.items)}</p>
                      </td>
                      <td className="p-3 text-right font-semibold text-orange-700 text-xs">${calcCost(req.items).toFixed(2)}</td>
                      <td className="p-3 text-xs">{req.preferred_date || "-"}</td>
                      <td className="p-3">{statusBadge(req.status)}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          {req.status === "pending" && (
                            <ConfirmAction
                              title="Confirm Shipping?"
                              description={`Confirm shipping request for ${req.full_name}?`}
                              onConfirm={() => updateShippingStatus(req.id, "confirmed")}
                              confirmText="Yes, Confirm"
                            >
                              <Button size="sm" variant="outline" className="text-blue-600 text-xs h-7"
                                data-testid={`confirm-shipping-${req.id}`}>
                                Confirm
                              </Button>
                            </ConfirmAction>
                          )}
                          {req.status === "confirmed" && (
                            <ConfirmAction
                              title="Mark as Picked Up?"
                              description={`Mark shipping for ${req.full_name} as picked up?`}
                              onConfirm={() => updateShippingStatus(req.id, "picked_up")}
                              confirmText="Yes, Picked Up"
                            >
                              <Button size="sm" variant="outline" className="text-purple-600 text-xs h-7">
                                Picked Up
                              </Button>
                            </ConfirmAction>
                          )}
                          {req.status === "picked_up" && (
                            <ConfirmAction
                              title="Mark as Delivered?"
                              description={`Mark shipping for ${req.full_name} as delivered?`}
                              onConfirm={() => updateShippingStatus(req.id, "delivered")}
                              confirmText="Yes, Delivered"
                            >
                              <Button size="sm" variant="outline" className="text-green-600 text-xs h-7">
                                Delivered
                              </Button>
                            </ConfirmAction>
                          )}
                          <a href={`/bill-of-lading?name=${encodeURIComponent(req.full_name)}&phone=${encodeURIComponent(req.phone_number)}&from=${encodeURIComponent(req.pickup_address)}`}
                            target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="ghost" className="text-orange-600 text-xs h-7" data-testid={`bol-${req.id}`}>
                              <FileText className="w-3 h-3 mr-1" /> BoL
                            </Button>
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bill of Lading Submissions */}
      <Card className="border-0 shadow-lg rounded-2xl" data-testid="bol-submissions-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <FileText className="w-5 h-5 text-emerald-600" />
            Bill of Lading Submissions ({(bolSubmissions || []).length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {(!bolSubmissions || bolSubmissions.length === 0) ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              No Bill of Lading submissions yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-emerald-50/50">
                    <th className="p-2.5 text-left font-medium">BoL #</th>
                    <th className="p-2.5 text-left font-medium">Sender</th>
                    <th className="p-2.5 text-left font-medium">Receiver / Customs Pickup</th>
                    <th className="p-2.5 text-left font-medium">Items</th>
                    <th className="p-2.5 text-left font-medium">Date</th>
                    <th className="p-2.5 text-right font-medium">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {bolSubmissions.map(bol => (
                    <tr key={bol.id} className="border-b hover:bg-emerald-50/30" data-testid={`bol-row-${bol.id}`}>
                      <td className="p-2.5 font-mono font-bold text-emerald-700">{bol.bolNumber || bol.id}</td>
                      <td className="p-2.5">
                        <p className="font-medium">{bol.shipFromName}</p>
                        <p className="text-[10px] text-muted-foreground">{bol.shipFromPhone}</p>
                        <p className="text-[10px] text-muted-foreground truncate max-w-[140px]">{bol.shipFromAddress}</p>
                      </td>
                      <td className="p-2.5">
                        <p className="font-medium text-emerald-800">{bol.receiverName}</p>
                        <p className="text-[10px] text-muted-foreground">{bol.receiverPhone1}{bol.receiverPhone2 ? ` / ${bol.receiverPhone2}` : ""}</p>
                        <p className="text-[10px] text-muted-foreground truncate max-w-[140px]">{bol.receiverAddress}</p>
                        {bol.receiverCityStateZip && <p className="text-[10px] text-muted-foreground">{bol.receiverCityStateZip}</p>}
                      </td>
                      <td className="p-2.5">
                        <p className="text-xs">{bol.itemsDescription || "-"}</p>
                        {bol.numberOfPieces && <p className="text-[10px] text-muted-foreground">{bol.numberOfPieces} pcs{bol.weight ? ` / ${bol.weight} lbs` : ""}</p>}
                      </td>
                      <td className="p-2.5">{bol.date || "-"}</td>
                      <td className="p-2.5 text-right">
                        <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 h-7 text-[10px]"
                          onClick={() => printShippingInvoice(bol, sp)}
                          data-testid={`invoice-${bol.id}`}>
                          <Printer className="w-3 h-3 mr-1" /> Invoice
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
